"use client";

import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ApiClientError } from "@/lib/api-client";
import { FadeInSection, MotionButton } from "@/components/animations/FadeIn";
import { SectionBackdrop } from "@/components/sections/SectionBackdrop";
import { Button } from "@/components/ui/tailwind-components";
import { OutlinedInput } from "@/components/ui/outlinedInput";
import { OutlinedTextarea } from "@/components/ui/outlinedTextArea";
import { useLocale } from "@/lib/locale/locale-provider";
import type { RsvpSectionProps } from "@/lib/sections/rsvp.type";
import { submitRsvp } from "@/src/features/wedding/api/rsvp";
import { rsvpSchema } from "@/src/features/wedding/schemas/rsvp-schema";

type YesNo = "yes" | "no" | "";

export function RsvpSection({
  title,
  subtitle,
  apiEndpoint: _apiEndpoint,
  deadline,
  labels = {},
}: RsvpSectionProps) {
  const locale = useLocale();
  const prefersReducedMotion = useReducedMotion();
  const fallbackLabels =
    locale === "fr"
      ? {
          firstName: "Prénom",
          lastName: "Nom",
          email: "E-mail",
          attendance: "Serez-vous présent(e) ?",
          guestCount: "Nombre d'invités",
          guestCountHint: "Merci d'indiquer le nombre total de personnes presentes, vous compris : 1 ou 2 maximum.",
          message: "Message (optionnel)",
          yes: "Oui",
          no: "Non",
          submit: "Envoyer le RSVP",
          submitting: "Envoi en cours...",
          thankYouTitle: "Merci beaucoup",
          thankYouText: "Merci pour votre réponse.",
          submitError: "Nous n'avons pas pu enregistrer votre réponse pour le moment. Merci de réessayer.",
          closedText: "La date limite de réponse est malheureusement dépassée.",
        }
      : locale === "en"
        ? {
            firstName: "First name",
            lastName: "Last name",
            email: "Email",
            attendance: "Will you attend?",
            guestCount: "Number of guests",
            guestCountHint: "Please choose the total number of people attending, including you: 1 or 2 only.",
            message: "Message (optional)",
            yes: "Yes",
            no: "No",
            submit: "Send RSVP",
            submitting: "Sending...",
            thankYouTitle: "Thank you",
            thankYouText: "Thank you for your reply.",
            submitError: "We could not save your reply right now. Please try again.",
            closedText: "The RSVP deadline has already passed.",
          }
        : {
            firstName: "Vorname",
            lastName: "Nachname",
            email: "E-Mail",
            attendance: "Wirst du teilnehmen?",
            guestCount: "Anzahl der Gaeste",
            guestCountHint: "Bitte waehle die Gesamtzahl der teilnehmenden Personen inklusive dir: nur 1 oder 2.",
            message: "Nachricht (optional)",
            yes: "Ja",
            no: "Nein",
            submit: "RSVP absenden",
            submitting: "Wird gesendet...",
            thankYouTitle: "Vielen Dank",
            thankYouText: "Danke fuer deine Rueckmeldung.",
            submitError: "Deine Rueckmeldung konnte gerade nicht gespeichert werden. Bitte versuche es noch einmal.",
            closedText: "Die Anmeldefrist ist abgelaufen.",
          };
  const resolvedLabels = { ...fallbackLabels, ...labels };

  const {
    firstName,
    lastName,
    email,
    attendance,
    guestCount,
    guestCountHint,
    message,
    yes,
    no,
    submit,
    submitting,
    thankYouTitle,
    thankYouText,
    submitError,
    closedText,
  } = resolvedLabels;

  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [emailValue, setEmailValue] = useState("");
  const [attendanceValue, setAttendanceValue] = useState<YesNo>("");
  const [guestCountValue, setGuestCountValue] = useState("1");
  const [note, setNote] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const isClosed = new Date() > new Date(deadline);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!first || !last || !emailValue || !attendanceValue || isClosed || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
        const payload = rsvpSchema.parse({
          fullName: `${first} ${last}`.trim(),
          email: emailValue.trim(),
          attendance: attendanceValue,
          guestCount:
            attendanceValue === "yes"
              ? Number.parseInt(guestCountValue, 10)
              : undefined,
          message: note.trim() || undefined,
        });

      const response = await submitRsvp(payload);
      setSuccessMessage(response.message);
      setSubmitted(true);
    } catch (error) {
      if (error instanceof ApiClientError) {
        setErrorMessage(error.message || submitError);
      } else if (error instanceof Error) {
        setErrorMessage(error.message || submitError);
      } else {
        setErrorMessage(submitError);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <SectionBackdrop tone="rsvp">
      <div className="mx-auto max-w-lg space-y-10">
        <FadeInSection className="space-y-3 text-center">
          <p className="inline-flex items-center justify-center rounded-full border border-white/60 bg-white/55 px-4 py-2 text-[0.72rem] font-semibold uppercase tracking-[0.22em] text-amber-800/80 shadow-[0_12px_28px_rgba(124,102,83,0.08)] backdrop-blur-xl">
            RSVP
          </p>
          <h2 className="font-display text-4xl text-foreground md:text-5xl">
            {title}
          </h2>
          {subtitle ? (
            <p className="mx-auto max-w-md text-sm leading-7 text-gray-600">
              {subtitle}
            </p>
          ) : null}
        </FadeInSection>

        <AnimatePresence mode="wait" initial={false}>
          {submitted ? (
            <motion.div
              key="rsvp-success"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 18, scale: 0.985 }}
              animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0, scale: 1 }}
              exit={prefersReducedMotion ? undefined : { opacity: 0, y: -12 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-[1.9rem] border border-white/65 bg-white/80 p-7 text-center shadow-[0_24px_64px_rgba(118,92,67,0.16)] backdrop-blur-2xl"
            >
              <h3 className="font-display text-2xl text-foreground">
                {thankYouTitle}
              </h3>
              <p className="mt-2 text-sm leading-7 text-gray-600">
                {successMessage ?? thankYouText}
              </p>
            </motion.div>
          ) : (
            <motion.form
              key="rsvp-form"
              onSubmit={handleSubmit}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 22 }}
              animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
              exit={prefersReducedMotion ? undefined : { opacity: 0, y: -12 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className={`space-y-6 rounded-[2rem] border border-white/70 bg-white/78 p-7 shadow-[0_28px_70px_rgba(118,92,67,0.14)] backdrop-blur-2xl ${
                isClosed ? "opacity-60" : ""
              }`}
              noValidate
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <OutlinedInput
                  label={firstName}
                  value={first}
                  onChange={setFirst}
                  autoComplete="given-name"
                  name="firstName"
                  required
                  disabled={isClosed || isSubmitting}
                />
                <OutlinedInput
                  label={lastName}
                  value={last}
                  onChange={setLast}
                  autoComplete="family-name"
                  name="lastName"
                  required
                  disabled={isClosed || isSubmitting}
                />
              </div>

              <OutlinedInput
                label={email}
                value={emailValue}
                onChange={setEmailValue}
                type="email"
                autoComplete="email"
                inputMode="email"
                name="email"
                required
                disabled={isClosed || isSubmitting}
              />

              <ChoiceGroup
                label={attendance}
                value={attendanceValue}
                yesLabel={yes}
                noLabel={no}
                disabled={isClosed || isSubmitting}
                onChange={(value) => {
                  setAttendanceValue(value);
                  setGuestCountValue("1");
                }}
              />

              <AnimatePresence initial={false}>
                {attendanceValue === "yes" && (
                  <motion.div
                    initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
                    animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
                    exit={prefersReducedMotion ? undefined : { opacity: 0, y: -8 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="space-y-3"
                  >
                    <GuestCountGroup
                      label={guestCount}
                      value={guestCountValue}
                      onChange={setGuestCountValue}
                      disabled={isClosed || isSubmitting}
                    />
                    <p className="text-xs leading-6 text-gray-500">
                      {guestCountHint}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              <OutlinedTextarea
                label={message}
                value={note}
                onChange={setNote}
                name="message"
                disabled={isClosed || isSubmitting}
              />

              {errorMessage ? (
                <p
                  role="alert"
                  className="rounded-2xl border border-red-200 bg-red-50/90 px-4 py-3 text-sm leading-6 text-red-600"
                >
                  {errorMessage}
                </p>
              ) : null}

              <motion.div
                whileHover={prefersReducedMotion ? undefined : { scale: 1.01, y: -1 }}
                whileTap={prefersReducedMotion ? undefined : { scale: 0.985 }}
                transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
              >
                <Button
                  variant="wedding"
                  className="w-full rounded-xl font-semibold"
                  disabled={isClosed || isSubmitting}
                >
                  {isSubmitting ? submitting : submit}
                </Button>
              </motion.div>

              {isClosed ? (
                <p className="text-center text-xs font-medium text-red-500">
                  {closedText}
                </p>
              ) : null}
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </SectionBackdrop>
  );
}

function ChoiceGroup({
  label,
  value,
  yesLabel,
  noLabel,
  disabled,
  onChange,
}: {
  label: string;
  value: YesNo;
  yesLabel: string;
  noLabel: string;
  disabled?: boolean;
  onChange: (value: YesNo) => void;
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">{label}</legend>
      <div className="grid grid-cols-2 gap-3">
        <ChoiceButton active={value === "yes"} onClick={() => onChange("yes")} disabled={disabled}>
          {yesLabel}
        </ChoiceButton>
        <ChoiceButton active={value === "no"} onClick={() => onChange("no")} disabled={disabled}>
          {noLabel}
        </ChoiceButton>
      </div>
    </fieldset>
  );
}

function GuestCountGroup({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">{label}</legend>
      <div className="grid grid-cols-2 gap-3">
        {["1", "2"].map((option) => (
          <ChoiceButton
            key={option}
            active={value === option}
            onClick={() => onChange(option)}
            disabled={disabled}
          >
            {option}
          </ChoiceButton>
        ))}
      </div>
      <input type="hidden" name="guestCount" value={value} />
    </fieldset>
  );
}

function ChoiceButton({
  active,
  children,
  ...props
}: React.ComponentProps<typeof MotionButton> & { active?: boolean }) {
  const disabledClass = props.disabled
    ? "cursor-not-allowed opacity-60 hover:bg-white hover:shadow-none"
    : "";

  return (
    <MotionButton
      {...props}
      aria-pressed={active}
      className={`rounded-xl border px-4 py-3 text-sm transition ${
        active
          ? "border-amber-500 bg-amber-50 shadow-[0_12px_28px_rgba(214,170,76,0.12)]"
          : "border-gray-200 bg-white hover:bg-gray-50 hover:shadow-[0_10px_24px_rgba(0,0,0,0.06)]"
      } ${disabledClass}`}
    >
      {children}
    </MotionButton>
  );
}
